// src/supabase/snapshotService.ts - BULLETPROOF VERSION
import { supabase } from '../lib/supabaseClient';
import { logger } from '../utils/logger';
import type {
  RelinkFutureWeeksParams,
  RelinkFutureWeeksResponse,
  PropagateForwardFillParams,
  PropagateForwardFillResponse
} from '../types/supabase-rpc';

export interface SnapshotRecord {
  id: string;
  week_key: string;
  section_key: string;
  data: unknown; // Use unknown, then validate
  updated_at: string;
  user_id?: string;
  workspace_id?: string | null;
  // Forward-fill metadata
  is_forward_filled?: boolean;
  source_week_key?: string | null;
  forward_filled_by?: string | null;
  forward_filled_at?: string | null;
}

// --- VALIDATION & SAFETY HELPERS ----------------------------------------------

/** Recursively preserve all text nodes in TipTap document structure */
function preserveTextNodesRecursively(node: unknown): unknown {
  if (!node || typeof node !== 'object') {
    return node;
  }
  
  const nodeObj = node as Record<string, unknown>;
  
  // If this is a text node, preserve it exactly as-is
  if (nodeObj.type === 'text' && typeof nodeObj.text === 'string') {
    return nodeObj; // Preserve text exactly - no modification
  }
  
  // If this node has content, recursively process it
  if (Array.isArray(nodeObj.content)) {
    return {
      ...nodeObj,
      content: nodeObj.content.map(preserveTextNodesRecursively)
    };
  }
  
  // Otherwise, return node as-is
  return nodeObj;
}

/** Convert data to plain JSON to avoid serialization issues */
function toPlainJSON<T>(v: T): T {
  // Special handling for string data that should be converted to TipTap for notes constraint
  if (typeof v === 'string') {
    return {
      type: 'doc',
      content: [{
        type: 'paragraph',
        attrs: { textAlign: null },
        content: [{ type: 'text', text: v }]
      }]
    } as T;
  }

  try {
    // ✅ FIX: Ensure Unicode characters are preserved during serialization
    const serialized = JSON.stringify(v);
    const parsed = JSON.parse(serialized);

    // Special handling for TipTap data to ensure it meets database constraints
    if (parsed && typeof parsed === 'object' && parsed.type === 'doc') {
      // Ensure TipTap data has required structure for database constraints
      if (!parsed.content) {
        parsed.content = [];
      }
      if (!Array.isArray(parsed.content)) {
        parsed.content = [];
      }

      // Additional validation to ensure the constraint passes
      if (!parsed.attrs) {
        parsed.attrs = {};
      }

      // ✅ FIX: Recursively preserve ALL text nodes at any nesting level
      // This is critical for preserving Unicode characters (emojis, accented characters, etc.)
      // TipTap can split text into multiple nodes, especially with mixed Unicode characters
      if (Array.isArray(parsed.content)) {
        parsed.content = parsed.content.map(preserveTextNodesRecursively);
      }
    }

    return parsed as T;
  } catch (error) {
    logger.error('Error serializing data', { error }, 'snapshotService');
    return v; // Return original data if serialization fails
  }
}

/** Validate UUID format */
function isValidUUID(uuid: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

/** Validate workspace ID with comprehensive checks */
function validateWorkspaceId(workspaceId: string | null): string | null {
  if (!workspaceId) return null;
  
  // ✅ Accept the default workspace ID (configurable via env var)
  const DEFAULT_WORKSPACE = 
    import.meta.env.VITE_DEFAULT_WORKSPACE_ID || 
    '00000000-0000-0000-0000-000000000001';
  if (workspaceId === DEFAULT_WORKSPACE) return workspaceId;
  
  // Check if it's a valid UUID for other cases
  if (!isValidUUID(workspaceId)) {
    logger.warn('Invalid workspace_id format', { workspaceId }, 'snapshotService');
    return null;
  }
  
  // Check if it's not the all-zeros fallback value
  if (workspaceId === '00000000-0000-0000-0000-000000000000') {
    logger.warn('Using null workspace_id instead of zero UUID', undefined, 'snapshotService');
    return null;
  }
  
  return workspaceId;
}

/** Validate user ID with comprehensive checks */
function validateUserId(userId: string | null): string | null {
  if (!userId) return null;
  
  // Check if it's a valid UUID
  if (!isValidUUID(userId)) {
    logger.warn('Invalid user_id format', { userId }, 'snapshotService');
    return null;
  }
  
  return userId;
}

/** Get workspace ID with comprehensive validation and fallbacks */
function getWorkspaceId(): string {
  // Allow configuration via environment variable, fallback to default
  const DEFAULT_WORKSPACE_ID = 
    import.meta.env.VITE_DEFAULT_WORKSPACE_ID || 
    '00000000-0000-0000-0000-000000000001';
  
  try {
    const raw = localStorage.getItem('workspace:id');
    if (!raw || raw === 'null' || raw === 'undefined') {
      // ✅ Always return the default workspace ID, never null
      return DEFAULT_WORKSPACE_ID;
    }
    
    const validated = validateWorkspaceId(raw);
    if (!validated) {
      // Clear invalid workspace ID from localStorage
      localStorage.removeItem('workspace:id');
      // ✅ Return default workspace ID instead of null
      return DEFAULT_WORKSPACE_ID;
    }
    
    return validated;
  } catch (error) {
    logger.error('Error reading workspace:id from localStorage', { error }, 'snapshotService');
    // ✅ Return default workspace ID instead of null
    return DEFAULT_WORKSPACE_ID;
  }
}

/** Apply tenant filter with comprehensive validation and error handling */
function withTenantFilter<T>(qb: T, user_id: string | null): T {
  try {
    const validatedUserId = validateUserId(user_id);
    const workspaceId = getWorkspaceId();
    
    // Log the filtering strategy for debugging
    if (import.meta.env.DEV) {
      logger.debug('Tenant filtering', {
        workspaceId,
        userId: validatedUserId,
        strategy: workspaceId ? 'workspace' : 'user'
      }, 'snapshotService');
    }
    
    // Type assertion needed because Supabase query builder types don't expose all filter methods
    const builder = qb as unknown as {
      or: (filter: string) => typeof qb;
      eq: (column: string, value: string | null) => typeof qb;
    };

    // ✅ PART 4: Tenant filter safety - workspace-only by default
    // Only include legacy user_id rows if explicitly configured for migration
    if (workspaceId && validatedUserId) {
      const includeLegacyUserRows = import.meta.env.VITE_INCLUDE_LEGACY_USER_ROWS === 'true';
      if (includeLegacyUserRows) {
        // Migration mode: include both workspace and user rows
        return builder.or(`workspace_id.eq.${workspaceId},user_id.eq.${validatedUserId}`);
      } else {
        // ✅ PART 4: Default to workspace-only (prevents mixing personal/workspace data)
        return builder.eq('workspace_id', workspaceId);
      }
    }

    // Priority 2: Use workspace_id if valid (works for both authenticated and unauthenticated users)
    if (workspaceId) {
      return builder.eq('workspace_id', workspaceId);
    }

    // Priority 3: Use user_id if valid (only for authenticated users)
    if (validatedUserId) {
      return builder.eq('user_id', validatedUserId);
    }
    
    // Priority 3: No filter (will hit RLS policies)
    logger.warn('No valid tenant filter available, relying on RLS', undefined, 'snapshotService');
    return qb;
  } catch (error) {
    logger.error('Error in withTenantFilter', { error }, 'snapshotService');
    // Return unfiltered query as fallback
    return qb;
  }
}

/** Create audit payload with comprehensive validation */
// @ts-expect-error - Unused function kept for future use
function createAuditPayload(
  weekKey: string, 
  sectionKey: string, 
  data: unknown, 
  user_id: string | null
): Partial<SnapshotRecord> | null {
  try {
    const validatedUserId = validateUserId(user_id);
    const workspaceId = getWorkspaceId();
    
    if (!validatedUserId) {
      logger.error('Cannot create audit payload without valid user_id', undefined, 'snapshotService');
      return null;
    }

    // ✅ ALWAYS include all 4 key columns to match DB unique constraint
    const payload: Partial<SnapshotRecord> = {
      user_id: validatedUserId,
      workspace_id: workspaceId,  // ✅ Always a string (never null - getWorkspaceId returns default)
      week_key: weekKey,
      section_key: sectionKey,
      data: toPlainJSON(data), // ✅ sanitize
      updated_at: new Date().toISOString(),
    };
    
    return payload;
  } catch (error) {
    logger.error('Error creating audit payload', { error }, 'snapshotService');
    return null;
  }
}

// --- AUDIT LOGGING ------------------------------------------------------------

/** Log data operations for audit trail */
function logDataOperation(
  operation: 'CREATE' | 'UPDATE' | 'DELETE' | 'READ',
  weekKey: string,
  sectionKey: string,
  userId: string | null,
  workspaceId: string | null,
  additionalData?: Record<string, unknown>
) {
  if (import.meta.env.DEV) {
    logger.debug(`${operation} operation`, {
      weekKey,
      sectionKey,
      userId,
      workspaceId,
      timestamp: new Date().toISOString(),
      ...additionalData
    }, 'snapshotService');
  }
}

// --- CRUD OPERATIONS WITH BULLETPROOF ERROR HANDLING -------------------------

/** Result type for getSnapshot that includes row existence information */
export type SnapshotResult = {
  data: unknown;
  rowExists: boolean;
};

/** Get a snapshot for a specific week and section (shared row approach)
 * 
 * Returns an object with `data` and `rowExists` to distinguish between:
 * - Row doesn't exist (rowExists: false, data: null)
 * - Row exists but data is null/empty (rowExists: true, data: null)
 * 
 * This is critical for forward-fill logic to know whether to forward-fill
 * (when row doesn't exist) vs preserve historical data (when row exists).
 */
export async function getSnapshot(weekKey: string, sectionKey: string, explicitWorkspaceId?: string): Promise<SnapshotResult | null> {
  try {
    // Validate inputs
    if (!weekKey || !sectionKey) {
      logger.error('Invalid inputs', { weekKey, sectionKey }, 'snapshotService');
      return null;
    }

    const workspaceId = explicitWorkspaceId ?? getWorkspaceId();
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) {
      logger.error('Auth error while resolving tenant', { error: userError }, 'snapshotService');
    }
    const user_id = userData?.user?.id ?? null;
    
    // Log triple key for debugging (only in development and reduce frequency)
    if (import.meta.env.DEV && Math.random() < 0.1) {
      logger.debug('Triple key', { 
        workspaceId: workspaceId, 
        section: sectionKey, 
        periodKey: weekKey 
      }, 'snapshotService');
    }
    
    logDataOperation('READ', weekKey, sectionKey, user_id, workspaceId);
    
    // Query using tenant-aware filter (workspace_id preferred, fallback to user_id)
    let qb = supabase
      .from('snapshots')
      .select('id, data, updated_at, user_id')  // user_id now = "last editor"
      .eq('week_key', weekKey)
      .eq('section_key', sectionKey);

    qb = withTenantFilter(qb, user_id);

    const { data, error } = await qb.maybeSingle();  // ← important: use maybeSingle instead of single

    // PGRST116 = no rows; not an error
    if (error && error.code !== 'PGRST116') {
      logger.error('Query error', { errorMessage: (error as { message?: string })?.message, errorCode: (error as { code?: string })?.code, weekKey, sectionKey }, 'snapshotService');
      return null;
    }

    if (data) {
      // Row exists - return data and rowExists=true
      const record = data as { id: string; user_id: string | null; updated_at: string; data: unknown };
      logDataOperation('READ', weekKey, sectionKey, null, workspaceId, { 
        found: true, 
        recordId: record.id,
        rowExists: true,
        lastEditor: record.user_id,
        updatedAt: record.updated_at
      });
      return {
        data: record.data,
        rowExists: true
      };
    }
    
    // Row doesn't exist - return rowExists=false
    logDataOperation('READ', weekKey, sectionKey, null, workspaceId, { 
      found: false,
      rowExists: false 
    });
    return {
      data: null,
      rowExists: false
    };
  } catch (error) {
    logger.error('Unexpected error', { error, weekKey, sectionKey }, 'snapshotService');
    return null;
  }
}

/** Save a snapshot with shared row approach (3-column constraint) */
export async function saveSnapshot(
  weekKey: string, 
  sectionKey: string, 
  data: unknown,
  explicitWorkspaceId?: string
): Promise<boolean> {
  try {
    // Validate inputs
    if (!weekKey || !sectionKey) {
      logger.error('Invalid inputs', { weekKey, sectionKey }, 'snapshotService');
      return false;
    }

    const workspaceId = explicitWorkspaceId ?? getWorkspaceId();
    
    // Log triple key for debugging
    const payloadData = toPlainJSON(data);
    const payloadPreview = typeof payloadData === 'string' 
      ? payloadData.substring(0, 60) + (payloadData.length > 60 ? '...' : '')
      : JSON.stringify(payloadData).substring(0, 60) + '...';
    
    if (import.meta.env.DEV) {
      logger.debug('Triple key', { 
        workspaceId: workspaceId, 
        section: sectionKey, 
        periodKey: weekKey 
      }, 'snapshotService');
      logger.debug('Payload preview', { preview: payloadPreview }, 'snapshotService');
    }
    
    // Create payload for 3-column constraint (workspace_id, week_key, section_key)
    // Do NOT set user_id here - trigger will fill it automatically
    const payload = {
      workspace_id: workspaceId,
      week_key: weekKey,
      section_key: sectionKey,
      data: payloadData
    };

    logDataOperation('UPDATE', weekKey, sectionKey, null, workspaceId, { 
      hasWorkspace: !!workspaceId 
    });
    
    // Log the exact payload for debugging
    if (import.meta.env.DEV) {
      logger.debug('Payload', {
        workspace_id: workspaceId,
        week_key: weekKey,
        section_key: sectionKey,
        dataType: typeof data,
      }, 'snapshotService');
    }
    
    // Check if row is forward-filled before saving (for unlink detection)
    const existingResult = await getSnapshot(weekKey, sectionKey, workspaceId);
    const existing = existingResult?.data ?? null;
    let existingRecord: SnapshotRecord | null = null;
    let wasLinked = false;
    
    if (existingResult?.rowExists) {
      // Fetch full record to check is_forward_filled
      const { data: existingData, error: existingError } = await supabase
        .from('snapshots')
        .select('id, is_forward_filled, source_week_key, updated_at, forward_filled_at')
        .eq('workspace_id', workspaceId)
        .eq('week_key', weekKey)
        .eq('section_key', sectionKey)
        .maybeSingle();
      
      if (!existingError && existingData) {
        existingRecord = existingData as SnapshotRecord;
      }
    }
    
    // If user manually edited a forward-filled row, unlink it
    if (existingRecord?.is_forward_filled) {
      // Check if data actually changed (not just a re-save)
      const existingDataStr = JSON.stringify(existing);
      const newDataStr = JSON.stringify(payloadData);
      
      if (existingDataStr !== newDataStr) {
        // User manually edited - unlink from forward-fill
        wasLinked = true;
        // Type assertion needed because Partial<SnapshotRecord> doesn't include all optional fields
        // We need to add these fields to the payload object
        Object.assign(payload, {
          is_forward_filled: false,
          source_week_key: null,
          forward_filled_by: null,
          forward_filled_at: null
        } as Partial<SnapshotRecord>);
        
        if (import.meta.env.DEV) {
          logger.debug('Unlinking forward-filled row on manual edit', {
            weekKey,
            sectionKey,
            previousSource: existingRecord.source_week_key
          }, 'snapshotService');
        }
      }
    }
    
    // Upsert using 3-column constraint
    // Type assertion needed because Supabase types don't fully match our SnapshotRecord interface
    // The generated types are too strict, so we cast through unknown to never
    const { data: result, error } = await supabase
      .from('snapshots')
      .upsert(payload as unknown as never, {
        onConflict: 'workspace_id,week_key,section_key',
        ignoreDuplicates: false,
      })
      .select('workspace_id, week_key, section_key, data, updated_at, user_id, is_forward_filled, source_week_key')
      .single();

    if (error) {
      logger.error('Upsert error', { errorMessage: (error as { message?: string })?.message, errorCode: (error as { code?: string })?.code, weekKey, sectionKey }, 'snapshotService');
      // #region agent log (dev only — production CSP blocks 127.0.0.1:7242; unguarded fetch would throw and break save)
      if (import.meta.env.DEV) {
        fetch('http://127.0.0.1:7242/ingest/9f1fa8c7-8ce8-473c-bb6b-bdbbaff33d50',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'snapshotService.ts:493',message:'Upsert error - save failed',data:{error:error?.message||String(error),weekKey,sectionKey},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      }
      // #endregion
      return false;
    }
    
    const record = result as { id: string; week_key: string; section_key: string; updated_at: string; user_id: string | null; data: unknown; is_forward_filled: boolean };
    // #region agent log (dev only — production CSP blocks 127.0.0.1:7242; unguarded fetch would throw and break save)
    if (import.meta.env.DEV) {
      fetch('http://127.0.0.1:7242/ingest/9f1fa8c7-8ce8-473c-bb6b-bdbbaff33d50',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'snapshotService.ts:498',message:'Upsert succeeded',data:{weekKey,sectionKey,updated_at:record.updated_at},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    }
    // #endregion
    
    // Helpful debug during verification
    if (import.meta.env.DEV) {
      logger.debug('DB row after save', {
        wk: record.week_key, 
        sec: record.section_key, 
        updated_at: record.updated_at, 
        sample: record?.data
      }, 'snapshotService');
    }
    
    logDataOperation('UPDATE', weekKey, sectionKey, null, workspaceId, { 
      success: true,
      recordId: record.id,
      lastEditor: record.user_id,
      updatedAt: record.updated_at
    });

    // ✅ SAVE INVARIANT FIX: Re-link future weeks when a week is modified
    // CRITICAL: This is a SECONDARY side effect that must NEVER affect save success
    // - Isolated as fire-and-forget
    // - Errors are logged but never thrown
    // - Save success is determined ONLY by the primary upsert operation
    if (record.is_forward_filled === false) {
      // Explicitly mark as fire-and-forget using void to prevent unhandled rejections
      void (async () => {
        try {
          const updatedRows = await relinkFutureWeeksOnModification(sectionKey, weekKey, payloadData, workspaceId);
          if (updatedRows > 0) {
            if (import.meta.env.DEV) {
              logger.debug('Re-linked future weeks after modification', {
                sectionKey,
                modifiedWeek: weekKey,
                updatedRows,
                wasLinked: wasLinked
              }, 'snapshotService');
            } else {
              logger.info('[Forward-Fill] Re-linked future weeks', {
                section: sectionKey,
                modifiedWeek: weekKey,
                updatedRows
              });
            }
          }
        } catch (error) {
          // CRITICAL: Log error but NEVER throw - this is a secondary operation
          // Save success is determined by the primary upsert, not by this side effect
          logger.error('Re-linking error after modification (non-critical)', {
            error,
            sectionKey,
            modifiedWeek: weekKey
          }, 'snapshotService');
        }
      })();
    }

    return true;
  } catch (error) {
    logger.error('Unexpected error', { error, weekKey, sectionKey }, 'snapshotService');
    return false;
  }
}

/**
 * Save a snapshot with forward-fill metadata
 * Used when creating forward-filled rows
 */
export async function saveSnapshotWithForwardFillMetadata(
  weekKey: string,
  sectionKey: string,
  data: unknown,
  sourceWeekKey: string,
  forwardFilledBy: string,
  explicitWorkspaceId?: string
): Promise<boolean> {
  try {
    const workspaceId = explicitWorkspaceId ?? getWorkspaceId();
    const payloadData = toPlainJSON(data);
    
    const payload = {
      workspace_id: workspaceId,
      week_key: weekKey,
      section_key: sectionKey,
      data: payloadData,
      is_forward_filled: true,
      source_week_key: sourceWeekKey,
      forward_filled_by: forwardFilledBy,
      forward_filled_at: new Date().toISOString()
    };
    
    // Type assertion needed because Supabase types don't fully match our SnapshotRecord interface
    // The generated types are too strict, so we cast through unknown to never
    const { data: result, error } = await supabase
      .from('snapshots')
      .upsert(payload as unknown as never, {
        onConflict: 'workspace_id,week_key,section_key',
        ignoreDuplicates: false,
      })
      .select('id, is_forward_filled, source_week_key')
      .single();

    if (error) {
      logger.error('Forward-fill save error', { error, weekKey, sectionKey, sourceWeekKey }, 'snapshotService');
      return false;
    }
    
    if (import.meta.env.DEV) {
      logger.debug('Forward-fill saved with metadata', {
        weekKey,
        sectionKey,
        sourceWeekKey,
        isForwardFilled: (result as { is_forward_filled?: boolean })?.is_forward_filled
      }, 'snapshotService');
    }

    return true;
  } catch (error) {
    logger.error('Unexpected error in forward-fill save', { error, weekKey, sectionKey }, 'snapshotService');
    return false;
  }
}

/**
 * Re-link future weeks when a week transitions from linked to modified
 * When W44 is modified, all future linked weeks (W45, W46, etc.) are updated to point to W44
 */
export async function relinkFutureWeeksOnModification(
  sectionKey: string,
  modifiedWeekKey: string,
  newData: unknown,
  explicitWorkspaceId?: string
): Promise<number> {
  try {
    const workspaceId = explicitWorkspaceId ?? getWorkspaceId();
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    
    if (!userId) {
      logger.error('Cannot re-link without user ID', undefined, 'snapshotService');
      return 0;
    }
    
    const payloadData = toPlainJSON(newData);
    
    const params: RelinkFutureWeeksParams = {
      in_workspace_id: workspaceId,
      in_section_key: sectionKey,
      in_modified_week_key: modifiedWeekKey,
      in_new_data: payloadData,
      in_actor_user_id: userId
    };
    
    // Type assertion needed because Supabase RPC types are not fully generated
    // The RPC function signature is not in the generated types, so we cast to unknown first
    const { data, error } = await (supabase.rpc('relink_future_weeks_on_modification', params as unknown as never) as unknown as Promise<RelinkFutureWeeksResponse>);
    
    if (error) {
      logger.error('Re-linking error', { error, sectionKey, modifiedWeekKey }, 'snapshotService');
      return 0;
    }
    
    const updatedRows = data?.[0]?.updated_rows ?? 0;
    
    if (import.meta.env.DEV) {
      logger.debug('Future weeks re-linked', {
        sectionKey,
        modifiedWeekKey,
        updatedRows
      }, 'snapshotService');
    } else {
      logger.info('[Forward-Fill] Re-linked future weeks', {
        section: sectionKey,
        modifiedWeek: modifiedWeekKey,
        updatedRows
      });
    }
    
    return updatedRows;
  } catch (error) {
    logger.error('Unexpected error in re-linking', { error, sectionKey, modifiedWeekKey }, 'snapshotService');
    return 0;
  }
}

/**
 * Propagate forward-fill updates from a source week to all future weeks
 * - For linked weeks (is_forward_filled=true): replaces data
 * - For modified weeks (is_forward_filled=false): merges data intelligently by section type
 *   - Funnel/Recommended Creators/Notes: only propagate to weeks still linked to source
 *   - Live Assets/Campaign Spend/Promo Codes: propagate to ALL future weeks (cumulative)
 */
export async function propagateForwardFillUpdate(
  sectionKey: string,
  sourceWeekKey: string,
  newData: unknown,
  explicitWorkspaceId?: string
): Promise<number> {
  try {
    const workspaceId = explicitWorkspaceId ?? getWorkspaceId();
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    
    if (!userId) {
      logger.error('Cannot propagate without user ID', undefined, 'snapshotService');
      return 0;
    }
    
    const payloadData = toPlainJSON(newData);
    
    const params: PropagateForwardFillParams = {
      in_workspace_id: workspaceId,
      in_section_key: sectionKey,
      in_source_week_key: sourceWeekKey,
      in_new_data: payloadData,
      in_actor_user_id: userId
    };
    
    // Type assertion needed because Supabase RPC types are not fully generated
    // The RPC function signature is not in the generated types, so we cast to unknown first
    const { data, error } = await (supabase.rpc('propagate_forward_fill_update', params as unknown as never) as unknown as Promise<PropagateForwardFillResponse>);
    
    if (error) {
      logger.error('Propagation error', { error, sectionKey, sourceWeekKey }, 'snapshotService');
      return 0;
    }
    
    const updatedRows = data?.[0]?.updated_rows ?? 0;
    
    if (import.meta.env.DEV) {
      logger.debug('Forward-fill propagated', {
        sectionKey,
        sourceWeekKey,
        updatedRows
      }, 'snapshotService');
    } else {
      logger.info('[Forward-Fill] Propagated', {
        section: sectionKey,
        sourceWeek: sourceWeekKey,
        updatedRows
      });
    }
    
    return updatedRows;
  } catch (error) {
    logger.error('Unexpected error in propagation', { error, sectionKey, sourceWeekKey }, 'snapshotService');
    return 0;
  }
}

/**
 * Update asset approval status in future weeks only
 * When an asset's approval is toggled, this updates it in all FUTURE weeks (preserves past weeks as historical record)
 */
export async function updateAssetApprovalGlobally(
  assetId: string,
  approved: boolean,
  currentWeekKey: string,
  sectionKey: string = 'liveAssets',
  explicitWorkspaceId?: string
): Promise<number> {
  try {
    const workspaceId = explicitWorkspaceId ?? getWorkspaceId();
    
    // Import week comparison utility
    const { getWeekStartDateFromKey } = await import('../utils/weekKeyUtils');
    
    // Get the current week's date for comparison
    const currentWeekDate = getWeekStartDateFromKey(currentWeekKey);
    if (!currentWeekDate) {
      logger.error('Invalid current week key', { currentWeekKey }, 'snapshotService');
      return 0;
    }
    
    // Get all snapshots for this section (with high limit to get all weeks)
    const allSnapshots = await getSnapshotsBySection(sectionKey, undefined, 1000);
    
    if (!allSnapshots || allSnapshots.length === 0) {
      return 0;
    }
    
    let updatedCount = 0;
    
    // Update each snapshot that contains the asset (current week + future weeks, preserve past weeks)
    for (const snapshot of allSnapshots) {
      // Only update current week and future weeks (preserve past weeks as historical record)
      const snapshotWeekDate = getWeekStartDateFromKey(snapshot.week_key);
      if (!snapshotWeekDate) {
        continue; // Skip invalid week keys
      }
      
      // Only update if this week is current or future (preserve past weeks)
      if (snapshotWeekDate.getTime() < currentWeekDate.getTime()) {
        continue; // Skip past weeks only (preserve historical record)
      }
      // Update current week and all future weeks
      
      const assets = snapshot.data as unknown;
      
      // Ensure it's an array
      if (!Array.isArray(assets)) {
        continue;
      }
      
      // Check if this snapshot contains the asset
      const assetIndex = assets.findIndex((a: { id?: string }) => a?.id === assetId);
      
      if (assetIndex !== -1) {
        // Update the asset's approval status
        // Ensure we explicitly set approved (not undefined)
        const updatedAssets = [...assets];
        const currentAsset = updatedAssets[assetIndex] as { id?: string; approved?: boolean; [key: string]: unknown };
        updatedAssets[assetIndex] = {
          ...currentAsset,
          approved: approved // Explicitly set to true or false (not undefined)
        };
        
        // Save the updated snapshot
        const success = await saveSnapshot(
          snapshot.week_key,
          sectionKey,
          updatedAssets,
          workspaceId
        );
        
        if (success) {
          updatedCount++;
        }
      }
    }
    
    if (import.meta.env.DEV) {
      logger.debug('Updated asset approval in future weeks', {
        assetId,
        approved,
        currentWeek: currentWeekKey,
        updatedWeeks: updatedCount
      }, 'snapshotService');
    } else {
      logger.info('[Global Approval] Updated future weeks', {
        assetId,
        approved,
        currentWeek: currentWeekKey,
        updatedWeeks: updatedCount
      });
    }
    
    return updatedCount;
  } catch (error) {
    logger.error('Error updating asset approval globally', {
      error,
      assetId,
      approved,
      currentWeekKey
    }, 'snapshotService');
    return 0;
  }
}

/** Get all snapshots for a specific week */
export async function getSnapshotsByWeek(weekKey: string): Promise<Record<string, unknown> | null> {
  try {
    if (!weekKey) {
      logger.error('Invalid weekKey', { weekKey }, 'snapshotService');
      return null;
    }
    
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) {
      logger.error('Auth error', { error: userError }, 'snapshotService');
      return null;
    }
    
    const user_id = userData?.user?.id ?? null;
    const workspaceId = getWorkspaceId();
    
    logDataOperation('READ', weekKey, 'ALL_SECTIONS', user_id, workspaceId);
    
    let qb = supabase
      .from('snapshots')
      .select('section_key,data,user_id,workspace_id')
      .eq('week_key', weekKey);

    qb = withTenantFilter(qb, user_id);
    
    const { data, error } = await qb;
    if (error) {
      logger.error('Query error', { errorMessage: (error as { message?: string })?.message, errorCode: (error as { code?: string })?.code, weekKey }, 'snapshotService');
      return null;
    }

    if (!data?.length) {
      logDataOperation('READ', weekKey, 'ALL_SECTIONS', user_id, workspaceId, { 
        found: false 
      });
      return null;
    }

    const result: Record<string, unknown> = {};
    data.forEach((row: { section_key: string; data: unknown }) => { 
      result[row.section_key] = row.data;
    });
    
    logDataOperation('READ', weekKey, 'ALL_SECTIONS', user_id, workspaceId, { 
      found: true, 
      sectionCount: data.length 
    });

    return result;
  } catch (error) {
    logger.error('Unexpected error', { error, weekKey }, 'snapshotService');
    return null;
  }
}

/**
 * Get snapshots for a section, optionally filtered to only weeks before targetWeek
 * Optimized for forward-fill queries by limiting results and filtering by week
 * @param sectionKey - The section to query
 * @param targetWeekKey - Optional: only return weeks before this week (for forward-fill)
 * @param limit - Optional: limit number of results (default: 20 for performance)
 */
export async function getSnapshotsBySection(
  sectionKey: string,
  targetWeekKey?: string,
  limit: number = 20
): Promise<Array<{ week_key: string; data: unknown; updated_at?: string }> | null> {
  try {
    if (!sectionKey) {
      logger.error('Invalid sectionKey', { sectionKey }, 'snapshotService');
      return null;
    }
    
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) {
      logger.error('Auth error', { error: userError }, 'snapshotService');
      return null;
    }
    
    const user_id = userData?.user?.id ?? null;
    const workspaceId = getWorkspaceId();
    
    logDataOperation('READ', targetWeekKey || 'ALL_WEEKS', sectionKey, user_id, workspaceId);
    
    let qb = supabase
      .from('snapshots')
      .select('week_key,data,updated_at,user_id,workspace_id')
      .eq('section_key', sectionKey)
      .order('week_key', { ascending: false })
      .limit(limit);

    // If targetWeekKey is provided, only get weeks strictly before it (for forward-fill)
    // IMPORTANT: We use lexicographic comparison here for performance, but the hook will
    // do accurate date comparison to filter out edge cases (e.g., year boundaries).
    // This is safe because:
    // 1. Lexicographic comparison works correctly for same-year weeks (e.g., '2025-W40' < '2025-W45')
    // 2. The hook does date comparison to ensure accuracy (e.g., '2025-W01' vs '2024-W52')
    // 3. We fetch extra weeks (limit=20) and let the hook filter them accurately
    if (targetWeekKey) {
      // Filter to only weeks before target (lexicographically)
      // This is approximate but fast; the hook will do accurate date comparison
      qb = qb.lt('week_key', targetWeekKey);
    }

    qb = withTenantFilter(qb, user_id);

    const { data, error } = await qb;
    if (error) {
      // Check if this is an RLS policy error
      if (error.code === '42501' || error.message?.includes('policy') || error.message?.includes('permission')) {
        logger.error('RLS policy blocked query - user may not have access to workspace', {
          error,
          sectionKey,
          targetWeekKey,
          workspaceId,
          userId: user_id,
          errorCode: error.code,
          errorMessage: error.message
        }, 'snapshotService');
      } else {
        logger.error('Query error', { error, sectionKey, targetWeekKey }, 'snapshotService');
      }
      return null;
    }
    
    logDataOperation('READ', targetWeekKey || 'ALL_WEEKS', sectionKey, user_id, workspaceId, { 
      found: data?.length || 0,
      targetWeekKey,
      limited: limit
    });

    return data || null;
  } catch (error) {
    logger.error('Unexpected error', { error, sectionKey, targetWeekKey }, 'snapshotService');
    return null;
  }
}

/** Delete a snapshot with audit trail */
export async function deleteSnapshot(weekKey: string, sectionKey: string): Promise<boolean> {
  try {
    if (!weekKey || !sectionKey) {
      logger.error('Invalid inputs', { weekKey, sectionKey }, 'snapshotService');
      return false;
    }
    
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) {
      logger.error('Auth error', { error: userError }, 'snapshotService');
      return false;
    }
    
    const user_id = userData?.user?.id ?? null;
    const workspaceId = getWorkspaceId();
    
    logDataOperation('DELETE', weekKey, sectionKey, user_id, workspaceId);
    
    let qb = supabase
      .from('snapshots')
      .delete()
      .eq('week_key', weekKey)
      .eq('section_key', sectionKey);

    qb = withTenantFilter(qb, user_id);
    
    const { error } = await qb;
    if (error) {
      logger.error('Delete error', { error, weekKey, sectionKey }, 'snapshotService');
      return false;
    }
    
    logDataOperation('DELETE', weekKey, sectionKey, user_id, workspaceId, { 
      success: true 
    });

    return true;
  } catch (error) {
    logger.error('Unexpected error', { error, weekKey, sectionKey }, 'snapshotService');
    return false;
  }
}

/** Delete all snapshots for a week with audit trail */
export async function deleteSnapshotsByWeek(weekKey: string): Promise<boolean> {
  try {
    if (!weekKey) {
      logger.error('Invalid weekKey', { weekKey }, 'snapshotService');
      return false;
    }
    
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) {
      logger.error('Auth error', { error: userError }, 'snapshotService');
      return false;
    }
    
    const user_id = userData?.user?.id ?? null;
    const workspaceId = getWorkspaceId();
    
    logDataOperation('DELETE', weekKey, 'ALL_SECTIONS', user_id, workspaceId);
    
    // ✅ PHASE 1: Explicit tenant identity verification
    // CRITICAL: Log workspace_id BEFORE any operations to prove tenant identity
    if (import.meta.env.DEV) {
      logger.debug('[Phase1:TenantIdentity] Tenant identity confirmed', {
        weekKey,
        workspaceId,
        userId: user_id,
        workspaceIdType: typeof workspaceId,
        workspaceIdLength: workspaceId?.length || 0,
        workspaceIdValue: workspaceId || 'NULL'
      }, 'snapshotService');
    }
    
    // ✅ FIX: First verify rows exist before deletion WITH workspace_id
    let verifyQb = supabase
      .from('snapshots')
      .select('id, section_key, workspace_id, user_id')
      .eq('week_key', weekKey);
    
    verifyQb = withTenantFilter(verifyQb, user_id);
    
    const { data: existingRows, error: verifyError } = await verifyQb;
    if (verifyError) {
      logger.error('[Phase1:Delete] Verify error before delete', { 
        error: verifyError, 
        weekKey,
        workspaceId,
        userId: user_id
      }, 'snapshotService');
      return false;
    }
    
    const rowCountBefore = existingRows?.length || 0;
    
    // ✅ PHASE 1: Verify all rows belong to this workspace_id
    const rowsWithWorkspaceId = existingRows?.filter((r: { workspace_id?: string }) => r.workspace_id === workspaceId) || [];
    const rowsWithUserId = existingRows?.filter((r: { user_id?: string }) => r.user_id === user_id) || [];
    const rowsWithOtherWorkspace = existingRows?.filter((r: { workspace_id?: string }) => 
      r.workspace_id && r.workspace_id !== workspaceId
    ) || [];
    
    // ✅ PHASE 2: Instrument delete with comprehensive logging INCLUDING workspace_id breakdown
    const sectionsBefore = existingRows?.reduce((acc: Record<string, number>, r: { section_key?: string }) => {
      const section = r.section_key || 'unknown';
      acc[section] = (acc[section] || 0) + 1;
      return acc;
    }, {}) || {};
    
    if (import.meta.env.DEV) {
      logger.debug('[Phase1:Delete] Starting deletion with tenant identity', {
        weekKey,
        workspaceId,
        userId: user_id,
        rowCountBefore,
        rowsWithWorkspaceId: rowsWithWorkspaceId.length,
        rowsWithUserId: rowsWithUserId.length,
        rowsWithOtherWorkspace: rowsWithOtherWorkspace.length,
        sectionsBefore: JSON.stringify(sectionsBefore),
        workspaceIdBreakdown: {
          matching: rowsWithWorkspaceId.length,
          other: rowsWithOtherWorkspace.length,
          userIdOnly: rowsWithUserId.length - rowsWithWorkspaceId.length
        }
      }, 'snapshotService');
    }
    
    if (rowCountBefore === 0) {
      // No rows to delete - still return true (idempotent)
      if (import.meta.env.DEV) {
        logger.debug('[Phase2:Delete] No rows to delete', { weekKey }, 'snapshotService');
      }
      return true;
    }
    
    // ✅ PHASE 1: Perform deletion with explicit tenant filter and workspace_id verification
    // CRITICAL: Apply tenant filter BEFORE delete to ensure we only delete tenant's rows
    let qb = supabase
      .from('snapshots')
      .delete()
      .eq('week_key', weekKey);

    // Apply tenant filter BEFORE delete
    qb = withTenantFilter(qb, user_id);
    
    // ✅ FIX: Supabase delete() returns deleted rows when .select() is chained
    // Select workspace_id to verify tenant identity of deleted rows
    const { data: deletedData, error } = await qb.select('id, section_key, workspace_id, user_id');
    const deletedCount = deletedData?.length || 0;
    
    // ✅ PHASE 1: Verify deleted rows belong to this workspace_id
    const deletedWithWorkspaceId = deletedData?.filter((r: { workspace_id?: string }) => r.workspace_id === workspaceId) || [];
    const deletedWithOtherWorkspace = deletedData?.filter((r: { workspace_id?: string }) => 
      r.workspace_id && r.workspace_id !== workspaceId
    ) || [];
    
    if (error) {
      logger.error('[Phase1:Delete] Delete query error', { 
        error, 
        weekKey, 
        rowCountBefore,
        workspaceId,
        userId: user_id,
        errorCode: error.code,
        errorMessage: error.message
      }, 'snapshotService');
      return false;
    }
    
    if (import.meta.env.DEV) {
      logger.debug('[Phase1:Delete] Delete query executed with tenant verification', {
        weekKey,
        workspaceId,
        deletedCount,
        deletedWithWorkspaceId: deletedWithWorkspaceId.length,
        deletedWithOtherWorkspace: deletedWithOtherWorkspace.length,
        deletedSections: deletedData?.map((r: { section_key?: string }) => r.section_key) || [],
        workspaceIdBreakdown: {
          matching: deletedWithWorkspaceId.length,
          other: deletedWithOtherWorkspace.length
        }
      }, 'snapshotService');
    }
    
    // ✅ PHASE 1: Verify deletion succeeded by checking remaining rows WITH workspace_id
    // Use the SAME tenant filter to ensure we're checking the same scope
    let verifyAfterQb = supabase
      .from('snapshots')
      .select('id, section_key, workspace_id, user_id')
      .eq('week_key', weekKey);
    
    verifyAfterQb = withTenantFilter(verifyAfterQb, user_id);
    
    const { data: remainingRows, error: verifyAfterError } = await verifyAfterQb;
    if (verifyAfterError) {
      logger.warn('Post-delete verification query failed', { error: verifyAfterError }, 'snapshotService');
    }
    const rowCountAfter = remainingRows?.length || 0;
    
    // ✅ PHASE 1: Verify remaining rows belong to this workspace_id
    const remainingWithWorkspaceId = remainingRows?.filter((r: { workspace_id?: string }) => r.workspace_id === workspaceId) || [];
    const remainingWithOtherWorkspace = remainingRows?.filter((r: { workspace_id?: string }) => 
      r.workspace_id && r.workspace_id !== workspaceId
    ) || [];
    
    // ✅ PHASE 2: Log after-delete verification WITH workspace_id breakdown
    const sectionsAfter = remainingRows?.reduce((acc: Record<string, number>, r: { section_key?: string }) => {
      const section = r.section_key || 'unknown';
      acc[section] = (acc[section] || 0) + 1;
      return acc;
    }, {}) || {};
    
    if (import.meta.env.DEV) {
      logger.debug('[Phase1:Delete] After-delete verification with tenant identity', {
        weekKey,
        workspaceId,
        rowCountBefore,
        deletedCount,
        rowCountAfter,
        remainingWithWorkspaceId: remainingWithWorkspaceId.length,
        remainingWithOtherWorkspace: remainingWithOtherWorkspace.length,
        sectionsAfter: JSON.stringify(sectionsAfter),
        remainingSections: remainingRows?.map((r: { section_key?: string }) => r.section_key) || [],
        workspaceIdBreakdown: {
          matching: remainingWithWorkspaceId.length,
          other: remainingWithOtherWorkspace.length
        }
      }, 'snapshotService');
    }
    
    // ✅ PHASE 1 REGRESSION TEST: After-delete DB count must be 0 for THIS workspace_id
    // CRITICAL: Only count rows matching THIS workspace_id
    if (remainingWithWorkspaceId.length > 0) {
      const remainingSections = remainingWithWorkspaceId.map((r: { section_key?: string }) => r.section_key) || [];
      logger.error('[PHASE1:REGRESSION TEST FAILED] Delete did not remove all rows for workspace_id', {
        weekKey,
        workspaceId,
        userId: user_id,
        rowCountBefore,
        deletedCount,
        rowCountAfter,
        remainingWithWorkspaceId: remainingWithWorkspaceId.length,
        remainingSections,
        sectionsAfter: JSON.stringify(sectionsAfter)
      }, 'snapshotService');
      
      // In DEV, also log detailed breakdown
      if (import.meta.env.DEV) {
        logger.error('[PHASE1:REGRESSION TEST] Remaining rows for workspace_id breakdown', {
          weekKey,
          workspaceId,
          remainingRows: remainingWithWorkspaceId.map((r: { section_key?: string; id?: string; workspace_id?: string }) => ({
            section: r.section_key,
            id: r.id,
            workspace_id: r.workspace_id
          })) || []
        }, 'snapshotService');
      }
      
      return false; // Fail the delete operation if rows remain for this workspace_id
    }
    
    // ✅ PHASE 1 SUCCESS: Zero rows for this workspace_id
    if (import.meta.env.DEV) {
      logger.debug('[PHASE1:SUCCESS] Delete successful - zero rows for workspace_id confirmed', {
        weekKey,
        workspaceId,
        rowCountBefore,
        deletedCount,
        rowCountAfter: 0,
        remainingWithWorkspaceId: 0
      }, 'snapshotService');
    }
    
    // ✅ PHASE 1: Final confirmation (already logged above, but keep for consistency)
    
    // ✅ BUCKET D FIX: Clear cache for ALL sections for this week_key
    try {
      const { Cache } = await import('../data/cache/LocalCache');
      Cache.clearWeek(weekKey);
      if (import.meta.env.DEV) {
        logger.debug('[Phase2:Delete] Cache cleared for week', { weekKey }, 'snapshotService');
      }
    } catch (cacheError) {
      // Non-critical - cache will expire naturally
      if (import.meta.env.DEV) {
        logger.debug('[Phase2:Delete] Cache clear failed (non-critical)', { weekKey, error: cacheError }, 'snapshotService');
      }
    }
    
    logDataOperation('DELETE', weekKey, 'ALL_SECTIONS', user_id, workspaceId, { 
      success: true,
      deletedCount,
      verified: true
    });

    return true;
  } catch (error) {
    logger.error('Unexpected error', { error, weekKey }, 'snapshotService');
    return false;
  }
}

/** Get all unique week keys for the current tenant */
export async function getAllWeekKeys(): Promise<string[]> {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) {
      // Don't log auth errors as errors - this is expected before login
      if (import.meta.env.DEV) {
        logger.debug('No authenticated user yet', { error: userError.message }, 'snapshotService');
      }
      return [];
    }
    
    const user_id = userData?.user?.id ?? null;
    const workspaceId = getWorkspaceId();
    
    let qb = supabase
      .from('snapshots')
      .select('week_key')
      .order('week_key', { ascending: false });

    qb = withTenantFilter(qb, user_id);
    
    const { data, error } = await qb;
    if (error) {
      logger.error('Query error', { errorMessage: (error as { message?: string })?.message, errorCode: (error as { code?: string })?.code }, 'snapshotService');
      return [];
    }

    const weekKeys = [...new Set(data?.map((row: { week_key: string }) => row.week_key) || [])];
    
    logDataOperation('READ', 'ALL_WEEKS', 'WEEK_KEYS', user_id, workspaceId, { 
      count: weekKeys.length 
    });
    
    return weekKeys;
  } catch (error) {
    logger.error('Unexpected error', { error }, 'snapshotService');
    return [];
  }
}

/** Get all unique section keys for the current tenant */
export async function getAllSectionKeys(): Promise<string[]> {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) {
      // Don't log auth errors as errors - this is expected before login
      if (import.meta.env.DEV) {
        logger.debug('No authenticated user yet', { error: userError.message }, 'snapshotService');
      }
      return [];
    }
    
    const user_id = userData?.user?.id ?? null;
    const workspaceId = getWorkspaceId();
    
    let qb = supabase
      .from('snapshots')
      .select('section_key')
      .order('section_key', { ascending: true });
    
    qb = withTenantFilter(qb, user_id);

    const { data, error } = await qb;
    if (error) {
      logger.error('Query error', { errorMessage: (error as { message?: string })?.message, errorCode: (error as { code?: string })?.code }, 'snapshotService');
      return [];
    }

    const sectionKeys = [...new Set(data?.map((row: { section_key: string }) => row.section_key) || [])];
    
    logDataOperation('READ', 'ALL_WEEKS', 'SECTION_KEYS', user_id, workspaceId, { 
      count: sectionKeys.length 
    });
    
    return sectionKeys;
  } catch (error) {
    logger.error('Unexpected error', { error }, 'snapshotService');
    return [];
  }
}

/** Check if a snapshot exists for the current tenant */
export async function snapshotExists(weekKey: string, sectionKey: string): Promise<boolean> {
  try {
    if (!weekKey || !sectionKey) {
      return false;
    }
    
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) {
      logger.error('Auth error', { error: userError }, 'snapshotService');
      return false;
    }
    
    const user_id = userData?.user?.id ?? null;
    const workspaceId = getWorkspaceId();
    
    let qb = supabase
      .from('snapshots')
      .select('id')
      .eq('week_key', weekKey)
      .eq('section_key', sectionKey)
      .maybeSingle();

    qb = withTenantFilter(qb, user_id);

    const { data, error } = await qb;
    if (error) {
      logger.error('Query error', { errorMessage: (error as { message?: string })?.message, errorCode: (error as { code?: string })?.code, weekKey, sectionKey }, 'snapshotService');
      return false;
    }

    const exists = !!data;
    
    logDataOperation('READ', weekKey, sectionKey, user_id, workspaceId, { 
      exists 
    });
    
    return exists;
  } catch (error) {
    logger.error('Unexpected error', { error }, 'snapshotService');
    return false;
  }
} 

// --- DATA MIGRATION HELPERS --------------------------------------------------

/** Migrate existing records to include audit fields */
export async function migrateExistingRecords(): Promise<{ migrated: number; errors: number }> {
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData?.user?.id) {
      logger.error('Auth error', { error: userError }, 'snapshotService');
      return { migrated: 0, errors: 1 };
    }
    
    const user_id = userData.user.id;
    // workspaceId could be used for future filtering if needed
    
    // Get all records that need migration (missing audit fields)
    let qb = supabase
      .from('snapshots')
      .select('id,user_id,workspace_id');
    
    qb = withTenantFilter(qb, user_id);
    
    const { data: records, error: queryError } = await qb;
    if (queryError) {
      logger.error('Query error', { error: queryError }, 'snapshotService');
      return { migrated: 0, errors: 1 };
    }
    
    if (!records?.length) {
      if (import.meta.env.DEV) {
        logger.debug('No records need migration', undefined, 'snapshotService');
      }
      return { migrated: 0, errors: 0 };
    }
    
    let migrated = 0;
    let errors = 0;
    
    // Update each record with audit fields
    for (const record of records) {
      try {
        // No migration needed - columns don't exist in current schema
        const updateError = null;
        
        if (updateError) {
          logger.error('Update error for record', { recordId: (record as { id?: string }).id, error: updateError }, 'snapshotService');
          errors++;
        } else {
          migrated++;
        }
      } catch (error) {
        logger.error('Error updating record', { recordId: (record as { id?: string }).id, error }, 'snapshotService');
        errors++;
      }
    }
    
    if (import.meta.env.DEV) {
      logger.debug('Migration complete', { migrated, errors }, 'snapshotService');
    }
    return { migrated, errors };
  } catch (error) {
    logger.error('Unexpected error', { error }, 'snapshotService');
    return { migrated: 0, errors: 1 };
  }
}

// --- HEALTH CHECK -------------------------------------------------------------

/** Check the health of the snapshot service */
export async function healthCheck(): Promise<{
  healthy: boolean;
  workspaceId: string | null;
  userId: string | null;
  canRead: boolean;
  canWrite: boolean;
  errors: string[];
}> {
  const errors: string[] = [];
  let healthy = true;
  
  try {
    // Check auth
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError) {
      errors.push(`Auth error: ${userError.message}`);
      healthy = false;
    }
    
    const user_id = userData?.user?.id ?? null;
    const workspaceId = getWorkspaceId();
    
    // Check read capability
    let canRead = false;
    try {
      const { error: readError } = await supabase
        .from('snapshots')
        .select('id')
        .limit(1);
      canRead = !readError;
      if (readError) {
        errors.push(`Read error: ${readError.message}`);
      }
    } catch (error) {
      errors.push(`Read test failed: ${error}`);
    }
    
    // Check write capability (only if we have a valid user)
    let canWrite = false;
    if (user_id) {
      try {
        const testData = { test: true, timestamp: Date.now() };
        const { error: writeError } = await supabase
          .from('snapshots')
          .insert({
            user_id,
            workspace_id: workspaceId,
            week_key: 'health-check',
            section_key: 'test',
            data: testData,
            updated_at: new Date().toISOString(),
          } as unknown as never);
        
        canWrite = !writeError;
        if (writeError) {
          errors.push(`Write error: ${writeError.message}`);
        } else {
          // Clean up test record
          await supabase
            .from('snapshots')
            .delete()
            .eq('week_key', 'health-check')
            .eq('section_key', 'test');
        }
      } catch (error) {
        errors.push(`Write test failed: ${error}`);
      }
    }
    
    if (!canRead || (user_id && !canWrite)) {
      healthy = false;
    }
    
    return {
      healthy,
      workspaceId,
      userId: user_id,
      canRead,
      canWrite,
      errors,
    };
  } catch (error) {
    errors.push(`Health check failed: ${error}`);
    return {
      healthy: false,
      workspaceId: null,
      userId: null,
      canRead: false,
      canWrite: false,
      errors,
    };
  }
}
